import type { Options, TooltipFormatterCallbackFunction } from 'highcharts';

/** HC v13 palette pin shape; not on `Options` until Highcharts 13 types are installed. */
export interface HighchartsPaletteOptions {
	palette: {
		colorScheme: 'light' | 'dark';
		light?: {
			backgroundColor?: string;
		};
	};
}

/** Pins HC v13 palette to report ThemeService mode (not OS `prefers-color-scheme`). */
export function highchartsPaletteForThemeMode(
	mode: 'light' | 'dark'
): HighchartsPaletteOptions {
	if (mode === 'light') {
		return {
			palette: {
				colorScheme: 'light',
				light: { backgroundColor: '#ffffff' },
			},
		};
	}
	return {
		palette: {
			colorScheme: 'dark',
		},
	};
}

/** Portaled HC tooltips read OS-driven palette unless colors are explicit. */
export function highchartsTooltipForThemeMode(
	mode: 'light' | 'dark'
): NonNullable<Options['tooltip']> {
	if (mode === 'dark') {
		return {
			backgroundColor: '#0f1526',
			borderColor: 'rgba(255, 255, 255, 0.16)',
			style: { color: 'rgba(255, 255, 255, 0.95)' },
		};
	}
	return {
		backgroundColor: '#ffffff',
		borderColor: 'rgba(0, 0, 0, 0.12)',
		style: { color: 'rgba(15, 21, 38, 0.87)' },
	};
}

/** Re-apply after Highcharts.merge so visualization JSON cannot override palette. */
export function pinHighchartsPaletteForThemeMode(
	options: Options,
	mode: 'light' | 'dark'
): Options {
	return {
		...options,
		...highchartsPaletteForThemeMode(mode),
	} as Options;
}

/** Palette + portaled tooltip shell and HTML formatter after all Highcharts.merge steps. */
export function pinHighchartsPaletteAndTooltipForThemeMode(
	options: Options,
	mode: 'light' | 'dark',
	tooltipFormatter?: TooltipFormatterCallbackFunction
): Options {
	const pinned = pinHighchartsPaletteForThemeMode(options, mode);
	const themeTooltip = highchartsTooltipForThemeMode(mode);
	const priorTooltip = pinned.tooltip ?? {};

	return {
		...pinned,
		tooltip: {
			...priorTooltip,
			...themeTooltip,
			useHTML: true,
			...(tooltipFormatter ? { formatter: tooltipFormatter } : {}),
			style: {
				...(typeof priorTooltip === 'object' && priorTooltip.style
					? priorTooltip.style
					: {}),
				...themeTooltip.style,
			},
		},
	} as Options;
}
